import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Column, Table, Typography } from '@uigovpe/components';
import { PageHeader } from '@/shared/components/PageHeader';
import { Skeleton } from '@/shared/components/Skeleton';
import { ErrorNotice } from '@/shared/components/ErrorNotice';
import { StatusTag } from '@/shared/components/StatusTag';
import { messageFromError } from '@/shared/api/apiError';
import { CertificateRequestSummary } from '@/shared/types/certificate-request';
import { formatDate, formatDateTime } from '@/shared/utils/formatting';
import { certificateRequestApi } from '../certificate-request/api/certificateRequestApi';

export function MyRequestsPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState<CertificateRequestSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;

    certificateRequestApi
      .list()
      .then((page) => active && setItems(page.items))
      .catch(
        (e) =>
          active &&
          setError(messageFromError(e, 'Não conseguimos carregar suas solicitações.')),
      )
      .finally(() => active && setLoading(false));

    return () => {
      active = false;
    };
  }, []);

  const rows = loading
    ? (Array.from({ length: 5 }, (_, i) => ({ id: `skeleton-${i}` })) as CertificateRequestSummary[])
    : items;

  return (
    <>
      <PageHeader
        eyebrow="Certidão Digital"
        title="Minhas solicitações"
        description="Acompanhe o andamento dos seus pedidos e retome rascunhos."
        onBack={() => navigate('/')}
      />

      <ErrorNotice message={error} />

      <Table
        value={rows}
        dataKey="id"
        emptyMessage="Você ainda não tem solicitações. Comece uma nova pelo início."
        stripedRows
        responsiveLayout="scroll"
      >
        <Column
          header="Protocolo"
          body={(row: CertificateRequestSummary) =>
            loading ? (
              <Skeleton width="8rem" />
            ) : (
              row.protocol ?? (
                <Typography variant="span" size="sm" className="cd-table__draft">
                  Rascunho não enviado
                </Typography>
              )
            )
          }
        />
        <Column
          header="Órgão"
          body={(row: CertificateRequestSummary) =>
            loading ? <Skeleton width="4rem" /> : row.agency
          }
        />
        <Column
          header="Ocorrência em"
          body={(row: CertificateRequestSummary) =>
            loading ? <Skeleton width="5rem" /> : formatDate(row.incidentDate)
          }
        />
        <Column
          header="Atualizada em"
          body={(row: CertificateRequestSummary) =>
            loading ? <Skeleton width="7rem" /> : formatDateTime(row.updatedAt)
          }
        />
        <Column
          header="Situação"
          body={(row: CertificateRequestSummary) =>
            loading ? <Skeleton width="5rem" /> : <StatusTag status={row.status} />
          }
        />
        <Column
          header=""
          body={(row: CertificateRequestSummary) =>
            loading ? (
              <Skeleton width="5rem" />
            ) : row.status === 'DRAFT' ? (
              <Button label="Continuar" text size="small" onClick={() => navigate('/request')} />
            ) : (
              <Button
                label="Ver detalhes"
                text
                size="small"
                onClick={() => navigate(`/my-requests/${row.id}`)}
              />
            )
          }
        />
      </Table>
    </>
  );
}
